import { useQuery } from '@tanstack/react-query';
import { fetchDisruptionRosterPage } from './api';
import {
  foldHotelBoard,
  hotelLabelFromUrn,
} from '@/components/hotels/build-hotel-board';
import type { HotelBoardBooking, IHotelBoard } from '@/components/hotels/types';

// EVENT-SCOPE adapter for the reusable hotel-occupancy board: walk the disruption
// roster, keep the PNR groups that landed a HOTEL, group them by `hotelUrn`, and
// hand them to the scope-neutral `foldHotelBoard` for enrichment. The board
// component (`@/components/hotels/hotel-occupancy-board`) renders the result. A
// case-scoped board would supply its own groups to the same `foldHotelBoard`.

// Re-exported for back-compat with existing importers/tests.
export { hotelLabelFromUrn };
export type { IHotelBoard, IHotelOccupancy } from '@/components/hotels/types';

const PAGE_LIMIT = 200;
const MAX_PAGES = 30;

/** Walk the disruption roster, fold HOTEL groups per-hotel, enrich with geo/spend.
 *  Exported for unit testing; the hook below is the consumer entry point. */
export async function buildHotelBoard(urn: string): Promise<IHotelBoard> {
  const byHotel = new Map<string, HotelBoardBooking[]>();
  const caseUrns = new Set<string>();
  let pendingPnrs = 0;
  let cursor: string | undefined;
  let pages = 0;
  let truncated = false;

  for (;;) {
    const page = await fetchDisruptionRosterPage(urn, {
      ...(cursor ? { cursor } : {}),
      limit: PAGE_LIMIT,
    } as Parameters<typeof fetchDisruptionRosterPage>[1]);
    for (const g of page.items) {
      if (g.accommodationKind !== 'HOTEL') continue;
      if (g.hotelUrn) {
        const list = byHotel.get(g.hotelUrn) ?? [];
        list.push(g);
        byHotel.set(g.hotelUrn, list);
        if (g.caseUrn) caseUrns.add(g.caseUrn);
      } else {
        pendingPnrs += 1; // needs a hotel, not yet allocated
      }
    }
    pages += 1;
    if (!page.nextCursor) break;
    if (pages >= MAX_PAGES) {
      truncated = true;
      break;
    }
    cursor = page.nextCursor;
  }

  return foldHotelBoard({
    groupsByHotel: byHotel,
    caseUrns,
    pendingPnrs,
    truncated,
    scopeUrn: urn,
  });
}

/**
 * The hotel-occupancy board for a disruption — hotels → their booked PNRs → guests,
 * enriched with stars / distance / rate. Lazy (only when `enabled`); cached 30s. Built
 * from the roster, so it reflects exactly what the Passengers grid shows as booked.
 */
export function useHotelBoard(urn: string | undefined, enabled = true) {
  return useQuery<IHotelBoard>({
    queryKey: ['disruptions', urn, 'hotel-board'],
    queryFn: () => buildHotelBoard(urn!),
    enabled: !!urn && enabled,
    staleTime: 30_000,
  });
}
